import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { 
  ChevronLeft, ChevronRight, User, Clock, Users, Calendar,
  ArrowLeftRight, Send, MessageSquare, CheckCircle2, XCircle, Loader2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const GIORNI_NOMI = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

const COLORI_FASCIA = {
  "#3B82F6": "bg-blue-500",
  "#10B981": "bg-emerald-500",
  "#F59E0B": "bg-amber-500",
  "#8B5CF6": "bg-violet-500",
  "#EC4899": "bg-pink-500",
  "#6366F1": "bg-indigo-500",
};

const MieiTurni = () => {
  const { user, token, localeCorrente, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [settimanaOffset, setSettimanaOffset] = useState(0);
  const [giorni, setGiorni] = useState([]);
  
  const [fasce, setFasce] = useState([]);
  const [turni, setTurni] = useState([]);
  const [operatori, setOperatori] = useState([]);
  
  const [vista, setVista] = useState(isAdmin ? "tutti" : "miei");

  // Cambio turno state
  const [cambioDialog, setCambioDialog] = useState(null); // turno object
  const [cambioTipo, setCambioTipo] = useState(null); // "collega" | "responsabile"
  const [cambioCollega, setCambioCollega] = useState(null);
  const [cambioNota, setCambioNota] = useState("");
  const [cambioSaving, setCambioSaving] = useState(false);
  
  // Le mie richieste
  const [mieRichieste, setMieRichieste] = useState([]);

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  useEffect(() => {
    const oggi = new Date();
    oggi.setDate(oggi.getDate() + settimanaOffset * 7);
    const day = oggi.getDay();
    const diff = oggi.getDate() - day + (day === 0 ? -6 : 1);
    const lunedi = new Date(oggi.setDate(diff));
    
    const fmtDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const g = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${g}`;
    };

    const giorniSettimana = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(lunedi);
      d.setDate(lunedi.getDate() + i);
      giorniSettimana.push({
        data: fmtDate(d),
        giorno: GIORNI_NOMI[i],
        giornoShort: GIORNI_NOMI[i].slice(0, 3),
        numero: d.getDate(),
        isOggi: d.toDateString() === new Date().toDateString()
      });
    }
    setGiorni(giorniSettimana);
  }, [settimanaOffset]);

  const loadData = useCallback(async () => {
    if (!localeCorrente?.id || giorni.length === 0) return;
    setLoading(true);
    
    const dataInizio = giorni[0]?.data;
    const dataFine = giorni[6]?.data;
    
    try {
      const headers = getAuthHeaders();
      
      const [fasceRes, turniRes] = await Promise.all([
        axios.get(`${API}/api/personale/fasce-turno?locale_id=${localeCorrente.id}`, { headers }),
        axios.get(`${API}/api/personale/turni?locale_id=${localeCorrente.id}&data_inizio=${dataInizio}&data_fine=${dataFine}`, { headers }),
      ]);
      
      setFasce(fasceRes.data || []);
      setTurni(turniRes.data || []);
      
      // Carica operatori (serve per selezione collega)
      try {
        const operatoriRes = await axios.get(`${API}/api/utenti?locale_id=${localeCorrente.id}`, { headers });
        setOperatori(operatoriRes.data.filter(u => u.ruolo !== 'admin') || []);
      } catch {
        setOperatori([]);
      }
      
      // Carica le mie richieste cambio turno
      if (user?.id) {
        try {
          const richRes = await axios.get(`${API}/api/personale/cambi-turno?locale_id=${localeCorrente.id}&utente_id=${user.id}`, { headers });
          setMieRichieste(richRes.data || []);
        } catch {
          setMieRichieste([]);
        }
      }
    } catch (error) {
      console.error("Errore caricamento:", error);
    } finally {
      setLoading(false);
    }
  }, [localeCorrente?.id, giorni, getAuthHeaders, user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Set di fasce ATTIVE per ogni giorno della settimana (mirror logica admin CalendarioTurni)
  // Una fascia è attiva per il giorno idx (0=Lun ... 6=Dom) se:
  //  - giorni_attivi è null/undefined → attiva sempre
  //  - giorni_attivi è array che contiene idx
  const fasceIdAttivePerGiorno = useMemo(() => {
    const map = {};
    giorni.forEach((g, idx) => {
      const ids = new Set();
      fasce.forEach(f => {
        if (!f.giorni_attivi || f.giorni_attivi.includes(idx)) {
          ids.add(f.id);
        }
      });
      map[g.data] = ids;
    });
    return map;
  }, [fasce, giorni]);

  // Filtro turno "visibile" identico all'admin: deve avere fascia_turno_id presente nelle fasce attive del giorno.
  // Turni custom (fascia_turno_id null/vuoto) sono SEMPRE visibili.
  const turnoVisibileIlGiorno = useCallback((t, dataGiorno) => {
    if (!t.fascia_turno_id) return true; // custom turno
    const attive = fasceIdAttivePerGiorno[dataGiorno];
    return attive ? attive.has(t.fascia_turno_id) : false;
  }, [fasceIdAttivePerGiorno]);

  const mieiTurni = useMemo(() => {
    if (!user?.id) return {};
    const miei = {};
    const seen = new Set(); // dedup per id turno
    giorni.forEach(g => {
      miei[g.data] = turni.filter(t => {
        if (t.data !== g.data || t.utente_id !== user.id) return false;
        if (!turnoVisibileIlGiorno(t, g.data)) return false;
        if (t.id && seen.has(t.id)) return false;
        if (t.id) seen.add(t.id);
        return true;
      });
    });
    return miei;
  }, [turni, giorni, user?.id, turnoVisibileIlGiorno]);

  const turniTutti = useMemo(() => {
    const tutti = {};
    giorni.forEach(g => {
      const turniGiorno = turni.filter(t => t.data === g.data && turnoVisibileIlGiorno(t, g.data));
      const perFascia = {};
      turniGiorno.forEach(t => {
        const fasciaKey = t.fascia_turno_id || 'other';
        if (!perFascia[fasciaKey]) {
          perFascia[fasciaKey] = {
            fascia_turno_id: t.fascia_turno_id,
            ora_inizio: t.ora_inizio,
            ora_fine: t.ora_fine,
            reparti: {},
            operatori: []
          };
        }
        const reparto = t.reparto || 'altro';
        if (!perFascia[fasciaKey].reparti[reparto]) {
          perFascia[fasciaKey].reparti[reparto] = [];
        }
        perFascia[fasciaKey].reparti[reparto].push({
          id: t.utente_id,
          nome: t.utente_nome,
          reparto: t.reparto
        });
        perFascia[fasciaKey].operatori.push({
          id: t.utente_id,
          nome: t.utente_nome,
          reparto: t.reparto
        });
      });
      tutti[g.data] = Object.values(perFascia);
    });
    return tutti;
  }, [turni, giorni, turnoVisibileIlGiorno]);

  const getFascia = (fasciaId) => fasce.find(f => f.id === fasciaId);

  const countMieiTurni = useMemo(() => {
    return Object.values(mieiTurni).reduce((acc, t) => acc + t.length, 0);
  }, [mieiTurni]);

  const rangeSettimana = useMemo(() => {
    if (giorni.length < 7) return "";
    const inizio = new Date(giorni[0].data);
    const fine = new Date(giorni[6].data);
    return `${inizio.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })} - ${fine.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}`;
  }, [giorni]);

  // Check if a request already exists for this shift
  const hasRichiesta = (turnoId) => {
    return mieRichieste.find(r => r.turno_id === turnoId && r.stato === "pending");
  };

  // Handle "Chiedi cambio" click
  const openCambioDialog = (turno) => {
    setCambioDialog(turno);
    setCambioTipo(null);
    setCambioCollega(null);
    setCambioNota("");
  };

  // Get colleagues that have a shift on the same day (for swap)
  const colleghiDisponibili = useMemo(() => {
    if (!cambioDialog) return [];
    return operatori.filter(op => 
      op.id !== user?.id && 
      turni.some(t => t.data === cambioDialog.data && t.utente_id === op.id)
    );
  }, [cambioDialog, operatori, turni, user?.id]);

  const handleInviaCambio = async () => {
    if (!cambioDialog || !cambioTipo) return;
    if (cambioTipo === "collega" && !cambioCollega) {
      toast.error("Seleziona un collega");
      return;
    }
    
    setCambioSaving(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API}/api/personale/cambi-turno?locale_id=${localeCorrente.id}&utente_id=${user.id}`,
        {
          turno_id: cambioDialog.id,
          tipo: cambioTipo,
          collega_id: cambioTipo === "collega" ? cambioCollega : null,
          nota: cambioNota || null,
        },
        { headers }
      );
      toast.success("Richiesta inviata!");
      setCambioDialog(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setCambioSaving(false);
    }
  };

  // Handle colleague responding to a swap request
  const handleRispondiCollega = async (richiestaId, accetta) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(
        `${API}/api/personale/cambi-turno/${richiestaId}/rispondi-collega?accetta=${accetta}`,
        null,
        { headers }
      );
      toast.success(accetta ? "Cambio accettato!" : "Cambio rifiutato");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  // Pending requests directed to me (as colleague)
  const richiestePerMe = useMemo(() => {
    return mieRichieste.filter(r => 
      r.collega_id === user?.id && r.stato === "pending"
    );
  }, [mieRichieste, user?.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={() => setSettimanaOffset(o => o - 1)}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="text-center">
            <p className="text-sm font-medium">{rangeSettimana}</p>
            {settimanaOffset === 0 && (
              <p className="text-xs text-muted-foreground">Questa settimana</p>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={() => setSettimanaOffset(o => o + 1)}>
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Richieste di scambio per me (come collega) */}
      {richiestePerMe.length > 0 && (
        <div className="px-4 pt-3 space-y-2">
          {richiestePerMe.map(r => (
            <div key={r.id} className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/5 space-y-2">
              <div className="flex items-start gap-2">
                <ArrowLeftRight className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{r.richiedente_nome} chiede di scambiare il turno</p>
                  <p className="text-xs text-muted-foreground">
                    {r.data} &middot; {r.turno_info}
                  </p>
                  {r.nota && <p className="text-xs text-muted-foreground mt-1 italic">"{r.nota}"</p>}
                </div>
              </div>
              <div className="flex gap-2 pl-6">
                <Button size="sm" className="h-8 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={() => handleRispondiCollega(r.id, true)}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Accetto
                </Button>
                <Button size="sm" variant="outline" className="h-8 flex-1 text-red-500 border-red-500/30 hover:bg-red-500/10 gap-1" onClick={() => handleRispondiCollega(r.id, false)}>
                  <XCircle className="h-3.5 w-3.5" /> Rifiuto
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Toggle vista */}
      <div className="px-4 py-3">
        <div className="flex gap-2 p-1 bg-muted rounded-xl">
          <button
            onClick={() => setVista("miei")}
            className={cn(
              "flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2",
              vista === "miei" 
                ? "bg-primary text-primary-foreground shadow-sm" 
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <User className="h-4 w-4" />
            I miei turni
            {countMieiTurni > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                {countMieiTurni}
              </Badge>
            )}
          </button>
          <button
            onClick={() => setVista("tutti")}
            className={cn(
              "flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2",
              vista === "tutti" 
                ? "bg-primary text-primary-foreground shadow-sm" 
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Users className="h-4 w-4" />
            Tutti i turni
          </button>
        </div>
      </div>

      {/* Vista "I miei turni" */}
      {vista === "miei" && (
        <div className="px-4 space-y-3">
          {countMieiTurni === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">Nessun turno assegnato questa settimana</p>
            </div>
          ) : (
            giorni.map(giorno => {
              const turniGiorno = mieiTurni[giorno.data] || [];
              if (turniGiorno.length === 0) return null;
              
              return (
                <div 
                  key={giorno.data}
                  className={cn(
                    "rounded-xl border overflow-hidden",
                    giorno.isOggi && "ring-2 ring-primary"
                  )}
                >
                  <div className={cn(
                    "px-4 py-2 flex items-center justify-between",
                    giorno.isOggi ? "bg-primary text-primary-foreground" : "bg-muted/50"
                  )}>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{giorno.giorno}</span>
                      <span className={cn(
                        "text-sm",
                        giorno.isOggi ? "text-primary-foreground/80" : "text-muted-foreground"
                      )}>
                        {giorno.numero}
                      </span>
                    </div>
                    {giorno.isOggi && (
                      <Badge variant="secondary" className="text-xs">Oggi</Badge>
                    )}
                  </div>
                  
                  <div className="divide-y">
                    {turniGiorno.map(turno => {
                      const fascia = getFascia(turno.fascia_turno_id);
                      const pending = hasRichiesta(turno.id);
                      return (
                        <div key={turno.id} className="p-4 flex items-center gap-3">
                          <div 
                            className={cn(
                              "w-2 h-12 rounded-full",
                              COLORI_FASCIA[fascia?.colore] || "bg-gray-400"
                            )}
                          />
                          <div className="flex-1">
                            <p className="font-medium">{fascia?.nome || turno.reparto || "Turno"}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className="h-3.5 w-3.5" />
                              <span>{turno.ora_inizio || "--:--"} - {turno.ora_fine || "--:--"}</span>
                            </div>
                          </div>
                          {pending ? (
                            <Badge variant="outline" className="text-amber-600 border-amber-500/30 text-xs">
                              In attesa
                            </Badge>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-xs gap-1 text-blue-500 hover:text-blue-600 hover:bg-blue-500/10"
                              onClick={() => openCambioDialog(turno)}
                              data-testid={`cambio-turno-${turno.id}`}
                            >
                              <ArrowLeftRight className="h-3.5 w-3.5" />
                              Cambio
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Vista "Tutti i turni" */}
      {vista === "tutti" && (
        <div className="px-4 space-y-4">
          {giorni.map(giorno => {
            const fasceTurno = turniTutti[giorno.data] || [];
            
            return (
              <div 
                key={giorno.data}
                className={cn(
                  "rounded-xl border overflow-hidden",
                  giorno.isOggi && "ring-2 ring-primary"
                )}
              >
                <div className={cn(
                  "px-4 py-2 flex items-center justify-between",
                  giorno.isOggi ? "bg-primary text-primary-foreground" : "bg-muted/50"
                )}>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{giorno.giornoShort}</span>
                    <span className={cn(
                      "text-sm",
                      giorno.isOggi ? "text-primary-foreground/80" : "text-muted-foreground"
                    )}>
                      {giorno.numero}
                    </span>
                  </div>
                  {giorno.isOggi && (
                    <Badge variant="secondary" className="text-xs">Oggi</Badge>
                  )}
                </div>
                
                {fasceTurno.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    Nessun turno
                  </div>
                ) : (
                  <div className="divide-y">
                    {fasceTurno.map((gruppo, idx) => {
                      const fascia = getFascia(gruppo.fascia_turno_id);
                      const isMio = gruppo.operatori.some(op => op.id === user?.id);
                      const totalOperatori = gruppo.operatori.length;
                      
                      return (
                        <div 
                          key={idx} 
                          className={cn("p-3", isMio && "bg-primary/5")}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className={cn(
                                "w-1.5 h-10 rounded-full",
                                COLORI_FASCIA[fascia?.colore] || "bg-gray-400"
                              )}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{fascia?.nome || "Turno"}</span>
                                  {isMio && (
                                    <Badge className="text-[10px] h-4 px-1.5 bg-primary/20 text-primary">
                                      TU
                                    </Badge>
                                  )}
                                </div>
                                <span className="text-xs text-muted-foreground">{totalOperatori} operatori</span>
                              </div>
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                <span>{gruppo.ora_inizio || "--:--"} - {gruppo.ora_fine || "--:--"}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="mt-2 pl-5 space-y-1.5">
                            {Object.entries(gruppo.reparti).map(([reparto, ops]) => (
                              <div key={reparto} className="flex items-center gap-2 flex-wrap">
                                <span className={cn(
                                  "text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded",
                                  reparto === 'cucina' ? "text-orange-600 bg-orange-100" :
                                  reparto === 'sala' ? "text-blue-600 bg-blue-100" :
                                  "text-gray-600 bg-gray-100"
                                )}>
                                  {reparto}:
                                </span>
                                {ops.map(op => (
                                  <span 
                                    key={op.id}
                                    className={cn(
                                      "text-xs px-2 py-0.5 rounded-full",
                                      op.id === user?.id 
                                        ? "bg-primary text-primary-foreground font-medium"
                                        : reparto === 'cucina' ? "bg-orange-50 text-orange-700 border border-orange-200"
                                        : reparto === 'sala' ? "bg-blue-50 text-blue-700 border border-blue-200"
                                        : "bg-muted text-muted-foreground"
                                    )}
                                  >
                                    {op.nome?.split(' ')[0]}
                                  </span>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ===== DIALOG CHIEDI CAMBIO TURNO ===== */}
      {cambioDialog && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={() => setCambioDialog(null)}>
          <div 
            className="bg-card w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-border shadow-xl max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-5 space-y-4">
              <div>
                <h3 className="font-bold text-lg">Chiedi cambio turno</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {cambioDialog.data} &middot; {getFascia(cambioDialog.fascia_turno_id)?.nome || "Turno"} ({cambioDialog.ora_inizio}-{cambioDialog.ora_fine})
                </p>
              </div>

              {/* Step 1: Scegli tipo */}
              {!cambioTipo && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">A chi vuoi chiedere?</p>
                  <button
                    onClick={() => setCambioTipo("collega")}
                    className="w-full p-4 rounded-xl border hover:border-blue-500/50 hover:bg-blue-500/5 transition-all text-left"
                    data-testid="cambio-tipo-collega"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <User className="h-5 w-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">A un collega</p>
                        <p className="text-xs text-muted-foreground">Proponi lo scambio direttamente a un collega</p>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setCambioTipo("responsabile")}
                    className="w-full p-4 rounded-xl border hover:border-purple-500/50 hover:bg-purple-500/5 transition-all text-left"
                    data-testid="cambio-tipo-responsabile"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-purple-500" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Al responsabile</p>
                        <p className="text-xs text-muted-foreground">Il responsabile deciderà con chi scambiare</p>
                      </div>
                    </div>
                  </button>
                </div>
              )}

              {/* Step 2a: Scegli collega */}
              {cambioTipo === "collega" && !cambioCollega && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Scegli un collega</p>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setCambioTipo(null)}>Indietro</Button>
                  </div>
                  {colleghiDisponibili.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">Nessun collega ha turni in questo giorno</p>
                  ) : (
                    colleghiDisponibili.map(op => {
                      const collegaTurno = turni.find(t => t.data === cambioDialog.data && t.utente_id === op.id);
                      const collegaFascia = collegaTurno ? getFascia(collegaTurno.fascia_turno_id) : null;
                      return (
                        <button
                          key={op.id}
                          onClick={() => setCambioCollega(op.id)}
                          className="w-full p-3 rounded-xl border hover:border-blue-500/50 hover:bg-blue-500/5 transition-all text-left flex items-center gap-3"
                        >
                          <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                            <User className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{op.nome || op.email || op.telefono}</p>
                            {collegaTurno && (
                              <p className="text-xs text-muted-foreground">
                                {collegaFascia?.nome || ""} {collegaTurno.ora_inizio}-{collegaTurno.ora_fine}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}

              {/* Step 2b/3: Nota + Invio */}
              {((cambioTipo === "collega" && cambioCollega) || cambioTipo === "responsabile") && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">
                      {cambioTipo === "collega" 
                        ? `Scambio con ${operatori.find(o => o.id === cambioCollega)?.nome || "collega"}`
                        : "Richiesta al responsabile"
                      }
                    </p>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => {
                      if (cambioTipo === "collega") setCambioCollega(null);
                      else setCambioTipo(null);
                    }}>Indietro</Button>
                  </div>
                  
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">Nota (opzionale)</label>
                    <textarea
                      value={cambioNota}
                      onChange={e => setCambioNota(e.target.value)}
                      placeholder="Es. Ho un impegno personale..."
                      className="w-full rounded-xl border bg-background p-3 text-sm min-h-[80px] resize-none focus:ring-2 focus:ring-primary/50 outline-none"
                      data-testid="cambio-nota"
                    />
                  </div>
                  
                  <Button 
                    className="w-full gap-2" 
                    onClick={handleInviaCambio}
                    disabled={cambioSaving}
                    data-testid="invia-cambio-btn"
                  >
                    {cambioSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Invia richiesta
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MieiTurni;
